<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter();

import { useAuthStore } from '@/stores/authStore.js';

const authService = useAuthStore();

function logout() {
  authService.logout();
  router.push('/login');
}

import companiesRequests from '@/requests/companiesRequests.js';
import Select from '@/components/Form/Select.vue';
import OverlayPanel from 'primevue/overlaypanel';
import Modal from '@/components/Modal.vue';

import {useCompanyStore} from '@/stores/companyStore';

const companyStore = useCompanyStore();
const { myCompanies: my_companies } = storeToRefs(companyStore);
const chosen_company = ref(companyStore.currentCompany || authService.user?.company_id);
const has_multiple_companies = ref(false);

// Состояние модального окна
const showAccessDeniedModal = ref(false);

// Проверка: является ли пользователь клиентом на сопровождении
const isClientOnSupport = computed(() => {
  return authService.user?.role === 'client' && authService.company?.procurement_rule_id;
});

// Обработчик клика по ссылке навигации
const handleNavClick = (link) => {
  if (link.isProcurement && isClientOnSupport.value) {
    showAccessDeniedModal.value = true;
    return;
  }
  router.push(link.to);
};

const options = [
  {
    label: 'Профиль',
    action: () => {},
    url: '/profile/my',
    isRedirect: true,
    class: 'menu-button',
  },
  {
    label: 'Журнал',
    action: () => {},
    url: '/audit-logs',
    isRedirect: true,
    class: 'menu-button',
  },
  {
    label: 'Выйти',
    action: logout,
    url: '/login',
    isRedirect: true,
    nenuzhenSep: true,
    class: 'menu-button exit',
  },
]

const profileMenu = ref(null);
const burgerMenu = ref(null);

const showProfileMenu = (event) => {
  profileMenu.value.show(event);
};

const hideProfileMenu = () => {
  profileMenu.value.hide();
};

const showBurgerMenu = (event) => {
  burgerMenu.value.show(event);
};

const hideBurgerMenu = () => {
  burgerMenu.value.hide();
};

watch(chosen_company, (newVal) => {
  companyStore.setCompany(newVal);
});

onMounted(async () => {
  if (!authService.user) {
    console.error('User data is not available');
    return;
  }

  try {
    if (authService.user.role !== 'client') {
      const my_companies_response = await companiesRequests.getMy().catch(err => {
        console.error('Failed to fetch companies:', err);
        throw err;
      });

      companyStore.setMyCompanies(my_companies_response.data.map(x => ({
        name: x.title ? x.title : 'id:' + x.id,
        value: x.id,
        obj: x
      })));

      has_multiple_companies.value = my_companies.value.length > 1;
      if (!has_multiple_companies.value && my_companies.value.length === 1) {
        chosen_company.value = my_companies.value[0].value;
        companyStore.setCompany(chosen_company.value);
        return;
      }

      if (!my_companies.value.some(x => x.value === authService.user.company_id)) {
        my_companies.value.push({
          name: authService.user.title,
          value: authService.user.company_id,
          obj: {id: authService.user.company_id, title: authService.user.title}
        });
      }

      try {
        const savedCompany = localStorage.getItem('selectedCompany');
        if (savedCompany && my_companies.value.some(c => c.value === savedCompany)) {
          chosen_company.value = savedCompany;
        }
      } catch (e) {
        console.error('LocalStorage access error:', e);
      }
    } else {
      companyStore.setMyCompanies([{
        name: authService.user.title,
        value: authService.user.company_id,
        obj: {id: authService.user.company_id, title: authService.user.title}
      }]);
      chosen_company.value = authService.user.company_id;
      companyStore.setCompany(chosen_company.value);
      has_multiple_companies.value = false;
    }
  } catch (error) {
    console.error('Error loading companies:', error);
  }
});

const isContractsActive = computed(() =>
  route.path.startsWith('/contracts')
)

const isProcurementRulesActive = computed(() =>
  route.path.startsWith('/procurementsRules')
)

const isCatalogsActive = computed(() =>
  route.path.startsWith('/catalog')
)

const isProcurementDocsActive = computed(() =>
  route.path.startsWith('/procurement_documents')
)

const isEisPlanItemActive = computed(() =>
  route.path.startsWith('/eisPlanItem')
)

const isProcurementActive = computed(() =>
  route.path.startsWith('/procurement/') || route.path === '/procurement'
)

const isProfileActive = computed(() =>
  route.path.startsWith('/profile')
);

const isAuditActive = computed(() =>
  route.path.startsWith('/audit-logs')
);

const mainNavLinks = [
  {
    to: '/procurementsRules/templates',
    label: 'Положение',
    isProcurementsRules: true,
    isCatalogs: true
  },
  {
    to: '/procurement',
    label: 'Закупки',
    isProcurementDocs: true,
    isEisPlanItem: true,
    isProcurement: true
  },
  {
    to: '/contracts/register',
    label: 'Договоры',
    isContracts: true
  }
];

// Проверка активности ссылки
const isLinkActive = (link) => {
  return (link.isContracts && isContractsActive.value) ||
    (link.isProcurementsRules && isProcurementRulesActive.value) ||
    (link.isCatalogs && isCatalogsActive.value) ||
    (link.isProcurementDocs && isProcurementDocsActive.value) ||
    (link.isEisPlanItem && isEisPlanItemActive.value) ||
    (link.isProcurement && isProcurementActive.value);
};

</script>

<template>
  <nav class="super-shadow navbar navbar-expand navbar-light bg-white d-flex justify-content-between ps-5 pe-5">
    <div class="d-flex align-items-center justify-content-center gap-4">
      <a href="/" class="text-decoration-none header-nav d-flex align-items-center">
        <img src="/mars.png" alt="Logo" class="d-block me-3" style="width: 75px;">
      </a>
      <nav class="flex-row gap-5 row-nav">
        <!-- Используем span вместо router-link для управляемой навигации -->
        <span
          v-for="link in mainNavLinks"
          :key="link.to"
          class="text-decoration-none lineHover text-dark nav-link-custom"
          :class="{ 'router-link-active': isLinkActive(link) }"
          @click="handleNavClick(link)"
        >
          {{ link.label }}
        </span>
      </nav>
      <nav class="burger-nav">
        <i class="bi bi-list font h1 cursor-pointer" @mouseenter="burgerMenu.toggle($event)"/>
        <OverlayPanel
          ref="burgerMenu"
          :style="{ 'margin-top': '30px' }"
          @mouseleave="burgerMenu.hide()"
        >
          <div class="d-flex flex-column gap-4 p-1" style="width: 140px">
            <div v-for="link in mainNavLinks" :key="link.to">
              <span
                class="text-decoration-none lineHover text-dark text nav-link-custom"
                :class="{ 'router-link-active': isLinkActive(link) }"
                @click="handleNavClick(link)"
              >
                {{ link.label }}
              </span>
            </div>
          </div>
        </OverlayPanel>
      </nav>
    </div>
    <div class="text-decoration-none header-nav">
      <div class="d-flex justify-content-end">
        <div class="flex-column me-2">
          <div class="d-flex justify-content-end align-items-center" style="gap: 40px">
            <div class="d-flex">
              <Select
                v-if="has_multiple_companies && authService.user.role != 'client'"
                v-model="chosen_company"
                :options="my_companies"
                :styles-obj="{
                    border: 'none !important',
                    'border-bottom': '1px solid #000',
                    'border-radius': '0px'
                  }"
                style="width: 400px;"
                placeholder="Выбрать заказчика"
                searchable
                can-clear
              />
            </div>
            <router-link
              to="/profile/my"
              style="text-decoration: none"
              class="d-flex align-items-center justify-content-center gap-1"
              @mouseenter="profileMenu.toggle($event)"
            >
              <i
                class="bi bi-person profile-icon cursor-pointer"
                style="display: flex; font-size: 30px; height: 38px; width: 38px; border-radius: 5px; align-items: center; justify-content: center"
              />
              <div class="d-flex flex-column text-end me-2 align-items-start vibor_zakazchika" style="height: 38px;">
                <span style="font-size: 14px;">
                  {{ authService.displayCompanyName }}
                </span>
                <span style="font-size: 12px;">
                  {{ authService.user.login }}
                </span>
              </div>
            </router-link>
            <OverlayPanel
              ref="profileMenu"
              @mouseleave="profileMenu.hide()"
              :style="{ 'margin-top': '30px' }"
            >
              <div class="d-flex flex-column align-items-center gap-4 p-1" style="width: 140px">
                <div
                  v-for="option in options"
                  :key="option.label"
                  @click="option.action"
                >
                  <router-link
                    v-if="option.isRedirect"
                    :to="option.url"
                    class="text-decoration-none lineHover text-dark"
                    :class="option.class"
                  >
                    {{ option.label }}
                  </router-link>
                </div>
              </div>
            </OverlayPanel>
          </div>
        </div>
      </div>
    </div>
  </nav>

  <Teleport to="body">
    <Modal v-if="showAccessDeniedModal" v-model="showAccessDeniedModal" title="Нет доступа" style="display: none;">
      <div class="d-flex flex-column align-items-center text-center p-3">
        <p style="font-size: 16px; line-height: 1.8;">
          Нет доступа. Обратитесь к курирующему специалисту по закупкам ЦОП ЮРИС
          (тел: +7 (3452) 215-100 + доб. №) (контакты размещены в АИС ЮРИС).
        </p>
        <button
          class="btn btn-primary mt-3"
          style="min-width: 100px;"
          @click="showAccessDeniedModal = false"
        >
          ОК
        </button>
      </div>
    </Modal>
  </Teleport>
</template>

<style scoped>
.super-shadow {
  -webkit-box-shadow: 0px 3px 13px 0px rgba(34, 60, 80, 0.2);
  -moz-box-shadow: 0px 3px 13px 0px rgba(34, 60, 80, 0.2);
  box-shadow: 0px 3px 13px 0px rgba(34, 60, 80, 0.2);
}

/* Стили для кастомных nav-ссылок */
.nav-link-custom {
  cursor: pointer;
  user-select: none;
}

.navbar-nav {
  gap: .5rem;
}

@media (min-width: 576px) {
  .navbar-nav {
    gap: .75rem;
  }
}

@media (min-width: 768px) {
  .navbar-nav {
    gap: 1rem;
  }
}

@media (min-width: 992px) {
  .navbar-nav {
    gap: 1.25rem;
  }
}

@media (min-width: 1200px) {
  .navbar-nav {
    gap: 1.5rem;
  }
}

@media (max-width: 1070px) {
  .row-nav {
    display: none;
  }

  .burger-nav {
    display: block;
  }
}

@media (min-width: 1070px) {
  .row-nav {
    display: flex;
  }

  .burger-nav {
    display: none;
  }
}

.navbar-nav .nav-link.nav-item:first-child {
  padding-left: 0 !important;
}

.navbar.bg-white {
  position: relative;
  z-index: 1040;
}

.dropdown.mb-1 {
  position: relative;
  z-index: 1039;
}

.dropdown-toggle {
  border: none;
}

.dropdown-toggle::after {
  display: none;
}

:deep(.dropdown-menu) {
  position: absolute;
  z-index: 1040 !important;
}

.navText {
  transition: color 0.3s ease;
}

.lineHover {
  position: relative;
  color: white;
}

.lineHover:hover > .navText {
  color: var(--bs-secondary);
}

.lineHover:hover::before {
  transform-origin: 0 50%;
  transform: scale3d(1, 1, 1);
}

.lineHover::before {
  transform-origin: 100% 50%;
  transform: scale3d(0, 1, 1);
  transition: transform 250ms;
  position: absolute;
  width: 100%;
  height: 2px;
  background: var(--bs-secondary);
  top: 100%;
  left: 0;
  pointer-events: none;
  content: '';
}

.lineHover.router-link-active::before,
.lineHover.router-link-exact-active::before {
  transform-origin: 0 50%;
  transform: scale3d(1, 1, 1);
}

.profile {
  color: black;
  cursor: pointer;
  transition: color 0.25s ease;
}

.profile * {
  transition: color 0.25s ease;
}

.profile-icon {
  font-size: 22px;
  line-height: 1;
}

.profile:hover {
  color: var(--bs-secondary);
}

.profile.active {
  color: var(--bs-secondary);
}

.point {
  width: 10px;
  height: 10px;
  background: var(--bs-secondary);
  border-radius: 50%;
  display: none;
}

.menu-button:hover + .point {
  display: block;
}

.exit {
  color: var(--bs-secondary) !important;
  font-weight: bold;
}

@media (max-width: 1300px) {
  .vibor_zakazchika {
    display: none !important;
  }
}
</style>